import React, {ChangeEvent, useEffect, useState} from 'react';
import {Button, ButtonGroup, Col, Form, Row, Stack, Table} from 'react-bootstrap';

interface Word {
  word: string;
  meanings: string[];
}

let words: { [key: string]: Word[] } = {};

function toWords(input: unknown): Word[] {
  const out: Word[] = [];

  if (Array.isArray(input)) {
    for (const item of input) {
      if (item && typeof item === "object") {
        const anyItem = item as any;
        const word = String(anyItem.word ?? "");
        if (!word) continue;
        const meaningsRaw = anyItem.meanings ?? anyItem.meaning;
        const meanings = Array.isArray(meaningsRaw)
            ? meaningsRaw.map(String)
            : meaningsRaw != null
                ? [String(meaningsRaw)]
                : [];
        if (meanings.length) out.push({ word, meanings });
      }
    }
    return out;
  }

  if (input && typeof input === "object") {
    for (const [dayKey, list] of Object.entries(
        input as Record<string, unknown>
    )) {
      if (!Array.isArray(list)) continue;
      const dayWords: Word[] = [];
      for (const item of list) {
        if (!item || typeof item !== "object") continue;
        const anyItem = item as any;
        const word = String(anyItem.word ?? "");
        if (!word) continue;
        const meaningsRaw = anyItem.meanings ?? anyItem.meaning;
        const meanings = Array.isArray(meaningsRaw)
            ? meaningsRaw.map(String)
            : meaningsRaw != null
                ? [String(meaningsRaw)]
                : [];
        if (meanings.length) dayWords.push({ word, meanings });
      }

      words[dayKey] = dayWords;
    }
    return Object.values(words).flat();
  }

  return out;
}

const WordTest: React.FC = () => {
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [currentWord, setCurrentWord] = useState<Word | null>(null);
  const [answer, setAnswer] = useState<string>('');
  const [isStarted, setIsStarted] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [history, setHistory] = useState<{
    word: string;
    myAnswer: string;
    correctAnswer: string;
    isCorrect: boolean
  }[]>([]);
  const [rate, setRate] = useState(0);
  const [file, setFile] = useState<File>();
  const [fileUrl, setFileUrl] = useState<String>("");
  const [bump, setBump] = useState(0);

  const [cycle, setCycle] = useState<Word[]>([]);

  // Fisher–Yates shuffle
  const shuffle = <T,>(arr: T[]): T[] => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // 현재 선택된 day들의 모든 단어를 모아 한 사이클 풀 생성
  const buildCycle = (keys: string[]): Word[] => {
    const pool: Word[] = [];
    keys.forEach((k) => {
      const arr = words[k];
      if (arr && arr.length) pool.push(...arr);
    });
    return shuffle(pool);
  };

  // 문제 리스트 선택 핸들러
  const handleListSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = event.target;
    setSelectedLists(prevSelectedLists => {
      if (prevSelectedLists.includes(value)) {
        return prevSelectedLists.filter(list => list !== value);
      } else {
        return [...prevSelectedLists, value];
      }
    });
    setCycle([]);
  };

  // 문제를 무작위로 선택하여 화면에 표시
  const chooseRandomWord = () => {
    const selectedListKeys = selectedLists.filter((list) => list in words);
    if (selectedListKeys.length === 0) {
      alert("문제 리스트를 선택해주세요.");
      return;
    }

    let currentCycle = cycle;
    if (currentCycle.length === 0) {
      // 새 사이클 생성
      currentCycle = buildCycle(selectedListKeys);
      if (currentCycle.length === 0) {
        alert("선택한 리스트에 문제가 없습니다.");
        return;
      }
    }

    const next = currentCycle[0];
    const rest = currentCycle.slice(1);
    setCycle(rest);            // 사이클에서 소진
    setCurrentWord(next);
    setAnswer("");
    setIsStarted(true);
    setTimeout(() => setAccepting(true), 100);
  };

  // 답 입력 시 정답 확인
  const checkAnswer = () => {
    if(!accepting) return;
    if (currentWord) {
      setAccepting(false);
      const userAnswers = answer.trim().toLowerCase().split(',').map(answer => answer.trim());
      const correctAnswers = currentWord.meanings.map(meaning => meaning.trim().toLowerCase());
      const isCorrect = userAnswers.every(userAnswer => correctAnswers.includes(userAnswer));
      setHistory([
        {
          word: currentWord.word,
          myAnswer: answer,
          correctAnswer: currentWord.meanings.join(', '),
          isCorrect,
        },
        ...history,
      ]);
      chooseRandomWord();
    }
  };

  // 엔터키 입력 시 답 확인
  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      checkAnswer();
    }
  };

  // 답안 이력 초기화
  const resetHistory = () => {
    setHistory([]);
    setRate(0)
  };

  // 정답률 계산
  const checkRate = () => {
    let correctCount: number = 0;
    let totalCount: number = history.length;
    history.map((item) => {
      if(item.isCorrect) {
        correctCount++;
      }
    });
    if(totalCount == 0) {
      return 0;
    }
    return Math.round((correctCount / totalCount) * 100 * 100) / 100;
  };

  useEffect(() => {
    setRate(checkRate());
  }, [history]);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // 파일 저장(선택사항)
    setFile(f);
    setFileUrl(URL.createObjectURL(f));

    // JSON만 허용
    if (!f.name.endsWith(".json")) {
      alert("JSON 파일만 업로드하세요.");
      return;
    }

    try {
      const text = await f.text();
      const raw = JSON.parse(text);
      // words 전역에 day별 저장
      toWords(raw);
      // 체크박스 목록 등 UI 반영을 위해 리렌더 트리거
      setBump((x) => x + 1);
      setCycle([]);
    } catch (err) {
      console.error("파일 파싱 실패:", err);
      alert("JSON 파싱에 실패했습니다.");
    }
  };

  return (
    <div style={{width: '40%', marginLeft: 'auto', marginRight: 'auto'}}>
      <Stack className={'justify-content-center limited-width mx-auto text-center'} gap={3}>
        <div>
          <h1>영단어 시험 프로그램 for Ion</h1>
          <p>Developed by.김채원</p>
        </div>
        <span>
          <Form.Control type="file" accept=".json,application/json" onChange={handleFileChange} />
          <a href="https://drive.google.com/file/d/1EwgE1AQhaQ5M6FbeNJPlFkN6gPKAYieE/view?usp=drive_link">파일 양식 다운로드</a>
        </span>
        <Form className={'text-start'}>
          <Row>
            {Object.keys(words).map((listKey, index) => (
              <Col key={`${listKey}-${index}-${bump}`} xs={6} sm={4} md={3} lg={2} className="mb-2">
                <Form.Check
                  type="checkbox"
                  id={`day-${index}`}
                  label={listKey}
                  value={listKey}
                  checked={selectedLists.includes(listKey)}
                  onChange={handleListSelection}
                />
              </Col>
            ))}
          </Row>
        </Form>
        <p>정답률 : {rate}%</p>
        <ButtonGroup className={'align-self-center'}>
          {!isStarted && (
            <Button onClick={chooseRandomWord}>시작</Button>
          )}
          {isStarted && (
            <Button variant="danger" onClick={resetHistory}>답안 이력 초기화</Button>
          )}
        </ButtonGroup>

        {currentWord && isStarted && (
          <>
            <h3>{currentWord.word}</h3>
            <Form.Control type="text"
                          placeholder="뜻을 입력하세요 (여러 개의 뜻일 경우 쉼표(,)로 구분)"
                          value={answer} onChange={(e) => setAnswer(e.target.value)}
                          onKeyUp={handleKeyUp}
            />
          </>
        )}

        <div>
          <p className="h3">답안 이력</p>
          <div className={'table-cover'}>
            <Table striped bordered hover>
              <thead>
              <tr>
                <th>영단어</th>
                <th>나의 답</th>
                <th>정답</th>
                <th>정답 여부</th>
              </tr>
              </thead>
              <tbody>
              {history.map((item, index) => (
                <tr key={index} className={item.isCorrect ? 'table-success' : 'table-danger'}>
                  <td>{item.word}</td>
                  <td>{item.myAnswer}</td>
                  <td>{item.correctAnswer}</td>
                  <td>{item.isCorrect ? 'O' : 'X'}</td>
                </tr>
              ))}
              </tbody>
            </Table>
          </div>
        </div>
      </Stack>
    </div>
  );
};

export default WordTest;
